import { useGetPostByUidQuery } from "@/store/api/postApi";
import { FC, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Post from "../Home/components/Post";
import PostComments, { editComment } from "./PostComments";
import { Box, Button, Divider, Typography } from "@mui/material";
import theme from "@/theme";
import CommentEditor from "./CommentEditor";

import { EditorState, CompositeDecorator, Modifier } from "draft-js";
import CustomLoader from "@/components/UI/CustomLoader/CustomLoader";
import { getToken } from "@/helpers/cookiesHelper";

interface ICommentDetailProps {}

const CommentDetail: FC<ICommentDetailProps> = ({}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const token = getToken();
  const uid = location.pathname.split("/post/")[1];
  const { data: post, isLoading } = useGetPostByUidQuery(uid!, {
    refetchOnMountOrArgChange: true,
  });
  const [replyUid, setReplyUid] = useState("");
  const [pendingText, setPendingText] = useState("");
  const [editPost, setEditPost] = useState<null | editComment>(null);
  const [localCommentsCount, setLocalCommentsCount] = useState(post?.commentsCount || 0);

  useEffect(() => {
    if (post) {
      setLocalCommentsCount(post.commentsCount);
    }
  }, [post]);

  const handleCommentCreated = () => {
    setLocalCommentsCount((prev) => prev + 1);
  };

  const MentionSpan = (props: any) => {
    return <span style={{ color: "#1976d2" }}>{props.children}</span>;
  };
  const findMentionEntities = (contentBlock: any, callback: any) => {
    const text = contentBlock.getText();
    const regex = /@\w+/g;
    let matchArr, start;
    while ((matchArr = regex.exec(text)) !== null) {
      start = matchArr.index;
      callback(start, start + matchArr[0].length);
    }
  };
  const decorator = new CompositeDecorator([
    {
      strategy: findMentionEntities,
      component: MentionSpan,
    },
  ]);
  const [editorState, setEditorState] = useState(EditorState.createEmpty(decorator));
  const [showPicker, setShowPicker] = useState(false);

  const handleEmojiClick = (emojiData: any) => {
    const contentState = editorState.getCurrentContent();
    const selectionState = editorState.getSelection();

    const newContentState = Modifier.insertText(contentState, selectionState, emojiData.emoji);

    const newEditorState = EditorState.push(editorState, newContentState, "insert-characters");

    setEditorState(newEditorState);
    setShowPicker(false);
  };

  return (
    <Box
      sx={{
        height: "calc(100vh - 54px)",
        display: "flex",
        flexDirection: "column",
        bgcolor: theme.palette.custom.mainWhite,
        overflow: "hidden",
      }}
    >
      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flex: 1,
          }}
        >
          <CustomLoader />
        </Box>
      ) : post ? (
        <>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }} className="custom-scrollbar">
              <Post post={{ ...post, commentsCount: localCommentsCount }} />
              <PostComments
                postId={post.uid}
                setPendingText={setPendingText}
                replyUid={replyUid}
                setReplyUid={setReplyUid}
                pendingText={pendingText}
                setEditPost={setEditPost}
                editPost={editPost}
                onCommentCreated={handleCommentCreated}
                isLiked={post.isLiked}
              />
            </Box>
            <Divider sx={{ width: "640px" }} />
            <Box
              sx={{
                zIndex: 10,
                padding: "0px 20px 20px 20px",
                backgroundColor: "#fff",
                flexShrink: 0,
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                mt: "12px",
              }}
            >
              {token ? (
                <CommentEditor
                  setReplyUid={setReplyUid}
                  replyUid={replyUid}
                  edit={editPost}
                  postId={post.uid}
                  editorState={editorState}
                  setEditorState={setEditorState}
                  showPicker={showPicker}
                  setShowPicker={setShowPicker}
                  handleEmojiClick={handleEmojiClick}
                  maxLength={1000}
                  variant="comment"
                  onCommentCreated={handleCommentCreated}
                  level={0}
                  isLiked={post.isLiked}
                />
              ) : (
                <Button
                  onClick={() => navigate("/login")}
                  sx={{
                    textTransform: "none",
                    color: theme.palette.custom.mainBlue,
                    fontWeight: 600,
                    fontSize: "15px",
                    padding: "12px",
                    borderRadius: "12px",
                    border: `1px solid ${theme.palette.custom.mainBlue}`,
                  }}
                >
                  Log in to comment
                </Button>
              )}
            </Box>
          </Box>
        </>
      ) : (
        <Box
          sx={{
            padding: "1rem",
            textAlign: "center",
            aspectRatio: "1",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Typography color="text.secondary" sx={{ fontSize: "28px" }}>
            Post not found
          </Typography>
          <Typography color="text.secondary" sx={{ fontSize: "24px" }}>
            404
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default CommentDetail;
